import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { TRANSLATIONS } from "./LanguageContext";

// parseAuthResponse runs outside any component (can't call useLanguage()), so
// it reads the persisted language choice directly, same key LanguageContext
// itself initializes from.
function getGenericErrorMessage(): string {
  let stored = null;
  try {
    stored = typeof window !== "undefined" ? localStorage.getItem("barcraft-language") : null;
  } catch (e) {
    // Ignore localStorage errors
  }
  return stored === "kz" ? TRANSLATIONS.kz.authGenericError : TRANSLATIONS.ru.authGenericError;
}

export type UserRole = "admin" | "customer";

export interface AuthUser {
  role: UserRole;
  name: string;
}

type AuthResult = { ok: true } | { ok: false; error: string; code?: string };

export interface RegisterInput {
  email: string;
  password: string;
  name: string;
  phone: string;
  company: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (login: string, password: string) => Promise<AuthResult>;
  register: (input: RegisterInput) => Promise<AuthResult>;
  logout: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<AuthResult>;
  resetPassword: (token: string, password: string) => Promise<AuthResult>;
  verifyEmail: (token: string) => Promise<AuthResult>;
  resendVerification: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function parseAuthResponse(res: globalThis.Response): Promise<AuthResult> {
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    return { ok: false, error: data?.error ?? getGenericErrorMessage(), code: data?.code };
  }
  return { ok: true };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch("/api/auth/me")
      .then((res) => (res.ok ? res.json() : null))
      .then((data: AuthUser | null) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      login: async (login, password) => {
        try {
          const res = await fetch("/api/auth/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ login, password }),
          });
          const result = await parseAuthResponse(res);
          if (result.ok) {
            const data = (await res.json().catch(() => null)) as AuthUser | null;
            if (data) setUser(data);
          }
          return result;
        } catch (e) {
          return { ok: false, error: getGenericErrorMessage() };
        }
      },
      register: async (input) => {
        try {
          const res = await fetch("/api/auth/register", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input),
          });
          return await parseAuthResponse(res);
        } catch (e) {
          return { ok: false, error: getGenericErrorMessage() };
        }
      },
      logout: async () => {
        try {
          await fetch("/api/auth/logout", { method: "POST" });
        } catch (e) {
          // Ignore logout network errors
        }
        setUser(null);
      },
      requestPasswordReset: async (email) => {
        try {
          const res = await fetch("/api/auth/request-password-reset", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email }),
          });
          return parseAuthResponse(res);
        } catch (e) {
          return { ok: false, error: getGenericErrorMessage() };
        }
      },
      resetPassword: async (token, password) => {
        try {
          const res = await fetch("/api/auth/reset-password", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token, password }),
          });
          return parseAuthResponse(res);
        } catch (e) {
          return { ok: false, error: getGenericErrorMessage() };
        }
      },
      verifyEmail: async (token) => {
        try {
          const res = await fetch(`/api/auth/verify-email?token=${encodeURIComponent(token)}`);
          const result = await parseAuthResponse(res);
          if (result.ok) {
            const data = (await res.json().catch(() => null)) as AuthUser | null;
            if (data) setUser(data);
          }
          return result;
        } catch (e) {
          return { ok: false, error: getGenericErrorMessage() };
        }
      },
      resendVerification: async (email) => {
        try {
          await fetch("/api/auth/resend-verification", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email }),
          });
        } catch (e) {
          // Ignore — the endpoint is intentionally silent either way
          // (anti-enumeration), so there's nothing to branch on here.
        }
      },
    }),
    [user, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
